import React, { Fragment, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import UniversalInput from '@lib/wrappers/UniversalInput';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';

// Types

import { HouseholdMembers, IsState, OnChangeArgTypes } from '@components/ssap/SsapTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

declare interface OutsideClaimsQuestionBlockProps {
    family: {
        outsideClaimers: Array<{
            filerAge: number,
            filerFullName: string,
            indexInHhmsArray: number
        }>
    },
    hasExternalFilers: boolean,
    hasNonCustodialParentClaimers: boolean,
    hasOtherThanSpouseOrParentClaimers: boolean,
    householdMembers: HouseholdMembers,
    isFormSubmitted: boolean,
    isFormOnTopLevelValid: boolean,
    isState: IsState,
    onFieldChange: OnChangeArgTypes,
    outsideClaimsInputStatuses: {
        firstQuestionInputs: {
            isAnswerProvided: boolean
        },
        secondQuestionInputs: {
            isAnswerProvided: boolean
        },
        thirdQuestionInputs: {
            isAnswerProvided: boolean
        }
    }
}


const OutsideClaimsQuestionBlock = (props: OutsideClaimsQuestionBlockProps): ReactElement | null => {
    const {
        family,
        hasExternalFilers,
        hasNonCustodialParentClaimers,
        hasOtherThanSpouseOrParentClaimers,
        householdMembers,
        isFormSubmitted,
        isFormOnTopLevelValid,
        isState,
        onFieldChange,
        outsideClaimsInputStatuses: {
            firstQuestionInputs,
            secondQuestionInputs,
            thirdQuestionInputs
        },
        ...restProps
    } = props;

    if(!isState.MN) {
        return null;
    }

    const getOutsideClaimers = () => {
        const eligibleHhms = family.outsideClaimers;

        if(eligibleHhms.length === 0) {
            return null;
        }

        const getDependentOnExternalFilerQuestion = ( ) => {
            const hhmsForQuestionOne = eligibleHhms.map(hhm => {
                const {
                    filerFullName,
                    indexInHhmsArray
                } = hhm;

                const isDependentOnExternalFiler = householdMembers[indexInHhmsArray]
                    .taxHouseholdComposition
                    .dependentOnExternalFiler;

                return (
                    <UniversalInput
                        key = { indexInHhmsArray }
                        label = { {
                            text: 'common.input.label.non_internationalized_text',
                            labelTextReplacements: {
                                text: filerFullName
                            }
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'isDependentOnExternalFiler',
                            classes: [ '' ],
                            checked: isDependentOnExternalFiler,
                            value: isDependentOnExternalFiler,
                            currentData: { },
                            disabled: false,
                            fields: [ ],
                        } }
                        inputActions = { {
                            onFieldChange
                        } }
                        indexInArray = { indexInHhmsArray }
                        { ...restProps }
                    />
                );
            });

            const noneOfAboveForQuestionOne = (
                <UniversalInput
                    key = 'hasExternalFilers'
                    label = { {
                        text: 'ssap.input.label.none_of_above'
                    } }
                    inputData = { {
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'hasExternalFilers',
                        classes: [ '' ],
                        checked: hasExternalFilers === false,
                        value: hasExternalFilers === false,
                        currentData: {},
                        fields: [ 'taxHousehold', 'thCommonParams' ],
                        reversedValue: true
                    } }
                    inputActions = { {
                        onFieldChange
                    } }
                    { ...restProps }
                />
            );

            return (
                <div className = 'subsection' >
                    <fieldset>
                        <legend className = 'required-true' data-testid = 'is_dependent_on_external_filer_legend'>
                            <FormattedMessage id = 'ssap.family_household.tax_composition.is_dependent_on_external_filer' />
                        </legend>
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid && !firstQuestionInputs.isAnswerProvided }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                        />
                        {
                            [
                                ...hhmsForQuestionOne,
                                noneOfAboveForQuestionOne
                            ]
                        }
                    </fieldset>
                </div>
            );
        };

        const getClaimedByNonCustodialParentQuestion = () => {
            const hhmsForQuestionTwo = eligibleHhms.reduce(
                (hhms, hhm) => {
                    const {
                        filerAge,
                        filerFullName,
                        indexInHhmsArray
                    } = hhm;

                    const isDependentOnExternalFiler = householdMembers[indexInHhmsArray]
                        .taxHouseholdComposition
                        .dependentOnExternalFiler;

                    const isClaimedByNonCustodialParent = householdMembers[indexInHhmsArray]
                        .taxHouseholdComposition
                        .claimedByNonCustodialParent;

                    if(filerAge >= 19 || !isDependentOnExternalFiler) {
                        return hhms;
                    } else {
                        return [
                            ...hhms,
                            <UniversalInput
                                key = { indexInHhmsArray }
                                label = { {
                                    text: 'common.input.label.non_internationalized_text',
                                    labelTextReplacements: {
                                        text: filerFullName
                                    }
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'isClaimedByNonCustodialParent',
                                    classes: [ '' ],
                                    checked: isClaimedByNonCustodialParent,
                                    value: isClaimedByNonCustodialParent,
                                    currentData: { },
                                    disabled: false,
                                    fields: [ ],
                                } }
                                inputActions = { {
                                    onFieldChange
                                } }
                                indexInArray = { indexInHhmsArray }
                                { ...restProps }
                            />
                        ];
                    }
                },
                [ ]
            );

            if(hhmsForQuestionTwo.length === 0) {
                return null;
            }

            const noneOfAboveForQuestionTwo = (
                <UniversalInput
                    key = 'hasNonCustodialParentClaimers'
                    label = { {
                        text: 'ssap.input.label.none_of_above'
                    } }
                    inputData = { {
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'hasNonCustodialParentClaimers',
                        classes: [ '' ],
                        checked: hasNonCustodialParentClaimers === false,
                        value: hasNonCustodialParentClaimers === false,
                        currentData: {},
                        fields: [ 'taxHousehold', 'thCommonParams' ],
                        reversedValue: true
                    } }
                    inputActions = { {
                        onFieldChange
                    } }
                    { ...restProps }
                />
            );

            return (
                <div className = 'subsection' >
                    <fieldset>
                        <legend className = 'required-true' data-testid = 'is_claimed_by_non_custodial_parent_legend'>
                            <FormattedMessage id = 'ssap.family_household.tax_composition.is_claimed_by_non_custodial_parent' />
                        </legend>
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid && !secondQuestionInputs.isAnswerProvided }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                        />
                        {
                            [
                                ...hhmsForQuestionTwo,
                                noneOfAboveForQuestionTwo
                            ]
                        }
                    </fieldset>
                </div>
            );
        };

        const getClaimedByOtherThanSpouseOrParentQuestion = () => {
            const hhmsForQuestionThree = eligibleHhms.reduce(
                (hhms, hhm) => {
                    const {
                        filerAge,
                        filerFullName,
                        indexInHhmsArray
                    } = hhm;

                    const isDependentOnExternalFiler = householdMembers[indexInHhmsArray]
                        .taxHouseholdComposition
                        .dependentOnExternalFiler;

                    const isClaimedByNonCustodialParent = householdMembers[indexInHhmsArray]
                        .taxHouseholdComposition
                        .claimedByNonCustodialParent;

                    const isClaimedByOtherThanSpouseOrParent = householdMembers[indexInHhmsArray]
                        .taxHouseholdComposition
                        .claimedByOtherThanSpouseOrParent;

                    if(filerAge >= 19 || isClaimedByNonCustodialParent || !isDependentOnExternalFiler) {
                        return hhms;
                    } else {
                        return [
                            ...hhms,
                            <UniversalInput
                                key = { indexInHhmsArray }
                                label = { {
                                    text: 'common.input.label.non_internationalized_text',
                                    labelTextReplacements: {
                                        text: filerFullName
                                    }
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'isClaimedByOtherThanSpouseOrParent',
                                    classes: [ '' ],
                                    checked: isClaimedByOtherThanSpouseOrParent,
                                    value: isClaimedByOtherThanSpouseOrParent,
                                    currentData: { },
                                    disabled: false,
                                    fields: [ ],
                                } }
                                inputActions = { {
                                    onFieldChange
                                } }
                                indexInArray = { indexInHhmsArray }
                                { ...restProps }
                            />
                        ];
                    }
                },
                [ ]
            );

            if(hhmsForQuestionThree.length === 0) {
                return null;
            }

            const noneOfAboveForQuestionThree = (
                <UniversalInput
                    key = 'hasOtherThanSpouseOrParentClaimers'
                    label = { {
                        text: 'ssap.input.label.none_of_above'
                    } }
                    inputData = { {
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'hasOtherThanSpouseOrParentClaimers',
                        classes: [ '' ],
                        checked: hasOtherThanSpouseOrParentClaimers === false,
                        value: hasOtherThanSpouseOrParentClaimers === false,
                        currentData: {},
                        fields: [ 'taxHousehold', 'thCommonParams' ],
                        reversedValue: true
                    } }
                    inputActions = { {
                        onFieldChange
                    } }
                    { ...restProps }
                />
            );

            return (
                <div className = 'subsection' >
                    <fieldset>
                        <legend className = 'required-true' data-testid = 'is_claimed_by_other_than_spouse_or_parent_legend'>
                            <FormattedMessage id = 'ssap.family_household.tax_composition.is_claimed_by_other_than_spouse_or_parent' />
                        </legend>
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid && !thirdQuestionInputs.isAnswerProvided }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                        />
                        {
                            [
                                ...hhmsForQuestionThree,
                                noneOfAboveForQuestionThree
                            ]
                        }
                    </fieldset>
                </div>
            );
        };

        return (
            <Fragment>
                { getDependentOnExternalFilerQuestion() }
                { getClaimedByNonCustodialParentQuestion() }
                { getClaimedByOtherThanSpouseOrParentQuestion() }
            </Fragment>
        );
    };

    return getOutsideClaimers();
};

export default OutsideClaimsQuestionBlock;